// ============================================================================
// EL GUION DE TU BOT — el comportamiento del bot, por secciones.
// T-GUI-10 cubrió R-GUI-01 (se ve por secciones) y R-GUI-03 (el dueño lo VE y
// no lo edita). T-GUI-11 abre R-GUI-02: **versiones y volver atrás**.
//
// ⭐ T-GUI-6 abre **R-GUI-10** de `embudo-sdd` (invariante I-9): el guion lo
// escribe el DUEÑO, no solo Victorix. La puerta de verdad no está aquí: está
// dentro de `chatai_app.guardar_guion` y `chatai_app.activar_version_guion`
// (migr `0056`, `soy_victorix() or es_dueno(p_tenant)`). Esta pantalla solo deja
// de ofrecer botones que la base iba a rechazar.
//
// ⛔⛔ EL AGENTE SIGUE SIN TOCAR NADA, Y ESO NO SE NEGOCIA. Para él la pantalla
// se comporta EXACTAMENTE como en T-GUI-10: una sola lectura de `prompts_bot`,
// ni un control de escritura, ni una consulta de más. Lo mide V-224.
// ⚠️ Y `puedeEditar` ENUMERA a quien puede (`victorix`/`dueno`); jamás
// `rol !== "agente"`, que hoy daría lo mismo y mañana abriría la puerta sola al
// primer rol nuevo.
//
// 🏚️ Lo que este tramo DEROGA: R-GUI-03 del SDD del entrenamiento («el dueño lo
// VE y no lo edita») y la mitad de `V-53` que lo medía para el dueño. Decisión
// de Cristhian del 2-sep-2026 (A-01): solo el dueño y Victorix.
//
// ⛔⛔ Y LO QUE SÍ CAMBIA: el guardar nace CON el versionado, nunca antes. Un
// botón de guardar sin «volver a la anterior» dejaría pisar en producción el
// texto que gobierna al bot que hoy vende, sin nada a lo que volver. Por eso
// aquí no hay ni un `update` sobre `prompts_bot`: las dos escrituras —apagar la
// activa y encender la otra— van dentro de UNA transacción, en las funciones de
// la migración `0051`. Dos llamadas desde el navegador dejarían al inquilino
// sin versión activa si la segunda no llega, y ahí el motor hace lo que debe:
// `EntrenamientoIncompleto`, el bot se calla en mitad de una venta. V-61 · V-63.
//
// ⛔ POR QUÉ SE PARTE POR ENCABEZADOS Y NO POR PÁRRAFOS (V-50). El valor de
// R-GUI-01 no es «trocear texto»: es poder mirar dos reglas juntas y ver que se
// contradicen. En GPT Maker el comportamiento son 2.973 caracteres en UNA caja,
// y ahí se escondió la regla que hacía que el bot soltara todos los precios de
// un colchón antes de saber la medida. Partir por líneas en blanco vuelve a
// mezclarlo todo, con otra ropa.
//
// ⭐ LO QUE NO ESTÁ REPARTIDO SE VE, NO SE ESCONDE (V-51) — Y AHORA TAMBIÉN SE
// GUARDA. El guion vivo de Living House son 367 caracteres SIN un solo
// encabezado (medido en producción el 1-sep-2026): si al recomponer el texto se
// escribieran solo los seis bloques conocidos, el guion entero desaparecería en
// el primer guardado, sin un solo error. Por eso «Todavía sin repartir» es una
// caja EDITABLE más, y `unirGuion` la escribe la primera.
//
// ⚠️ Y SIN GUION ACTIVO TAMPOCO SE CALLA (V-54): hoy solo Living House tiene
// fila en `prompts_bot`. Cualquier otro inquilino que entre tiene que leer una
// explicación, no una pantalla en blanco ni un error de Postgres.
// ============================================================================
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { FileText, RotateCcw } from "lucide-react";
import { Aviso, Boton, SkeletonLista } from "./piezas.tsx";
import {
  activarVersionGuion, explicarError, guardarGuion, leerGuionActivo, leerVersionesGuion,
} from "./datos.ts";
import { partirGuion, unirGuion } from "./tipos.ts";
import type {
  BloqueGuion, ContextoPanel, GuionActivo, GuionPartido, VersionGuion,
} from "./tipos.ts";

/** Cuánto tarda el worker en releer el entrenamiento (`worker/src/index.ts:832-837`). */
const MINUTOS_HASTA_QUE_EL_BOT_LO_TOMA = 15;

function fecha(iso: string): string {
  return new Date(iso).toLocaleDateString("es-CO", { day: "numeric", month: "short", year: "numeric" });
}

function fechaYHora(iso: string): string {
  return new Date(iso).toLocaleString("es-CO", {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

/**
 * Quién creó una versión.
 *
 * ⚠️ NO se resuelve el nombre de la persona, y es a propósito: `miembros` —la
 * única tabla con nombres a la que llega este panel— no contiene al staff de
 * Victorix de un negocio del que no es miembro. Lo que sí se distingue, y es lo
 * que se pregunta de verdad al mirar la lista, es si esa versión la dejaste TÚ.
 * 🏚️ HUECO DECLARADO (T-GUI-6): desde que el DUEÑO también escribe, una versión
 * que dejó OTRO dueño del mismo negocio aparece como «Victorix». Se deja así a
 * propósito —el tramo no toca esta función— y se anota para que no se lea como
 * un dato fiable: hoy ningún negocio tiene dos dueños.
 */
function quienLaCreo(v: VersionGuion, usuarioId: string | null): string {
  if (v.creado_por && usuarioId && v.creado_por === usuarioId) return "tú";
  return "Victorix";
}

/**
 * Por qué esta pantalla NO usa `explicarError` a secas (V-54).
 *
 * `explicarError` traduce el `42501` como «No tienes acceso a esta
 * conversación» (`datos.ts`), que aquí sería un diagnóstico FALSO: no hay
 * ninguna conversación de por medio, y el asesor se pondría a buscar el
 * problema donde no está. Un mensaje amable pero equivocado no es mejor que el
 * crudo: manda a la persona a otro lado.
 *
 * ⛔ Lo que NO se hace aquí es reimplementar la RLS: esto solo pone en español
 * lo que la base ya decidió.
 */
function explicarFalloDeGuion(e: any): string {
  const code = e?.code ?? "";
  const msg = typeof e?.message === "string" ? e.message : "";
  if (code === "42501" || /row-level security|permission denied/i.test(msg)) {
    return "Tu cuenta no puede ver el guion de este negocio. Pídeselo a tu contacto de Victorix.";
  }
  const traducido = explicarError(e);
  // Si `explicarError` no supo qué es, devuelve el crudo: eso no sale a pantalla.
  return traducido === msg
    ? "No se pudo cargar el guion. Vuelve a entrar en un momento."
    : traducido;
}

/**
 * Lo mismo, pero para el camino de ESCRITURA (T-GUI-11).
 *
 * ⚠️ Se lee el marcador `VX_*` del `detail`, no el texto del mensaje: el texto
 * cambia el día que alguien reescriba la migración, el marcador no.
 */
function explicarFalloAlGuardar(e: any): string {
  const detalle = `${e?.details ?? ""} ${e?.detail ?? ""} ${e?.hint ?? ""}`;
  if (detalle.includes("VX_SOLO_VICTORIX") || e?.code === "42501") {
    // T-GUI-6: la puerta la abre `soy_victorix() or es_dueno(p_tenant)` (migr
    // `0056`), así que quien lee esto es un AGENTE — o el dueño de otro negocio.
    // ⚠️ El marcador `VX_SOLO_VICTORIX` del `detail` NO se renombra: la 0056 lo
    // dejó igual a propósito y hay migraciones aplicadas que lo escriben.
    return "Tu cuenta no puede cambiar el guion de este negocio. Solo el dueño (o Victorix) lo escribe.";
  }
  if (detalle.includes("VX_GUION_VACIO")) {
    return "El guion quedó vacío, y un guion vacío deja al bot MUDO: deja de contestar. Escribe al menos una sección.";
  }
  if (detalle.includes("VX_VERSION_DESCONOCIDA")) {
    return "Esa versión ya no está en la lista. Vuelve a entrar para ver las que hay.";
  }
  return "No se pudo guardar. Vuelve a intentarlo en un momento; el guion sigue como estaba.";
}

/** T-ENT-GUION-2 (P4): el aviso que sale cuando el chat ya arma su guion desde las secciones. */
export const AVISO_TEXTO_ANTERIOR = "El chat ya usa las secciones de abajo; este texto es la versión anterior.";

export function Guion({ ctx, debajo, reemplazado = false }: {
  ctx: ContextoPanel; debajo?: ReactNode;
  /** T-ENT-GUION-2: el inquilino tiene secciones que el chat usa ⇒ este texto queda de solo lectura. */
  reemplazado?: boolean;
}) {
  const { sb, tenantId, rol, usuarioId } = ctx;
  // ⚠️ `esDueno` se usa para EXPLICAR; `puedeEditar` es lo que abre la escritura.
  // ⬇️ T-GUI-6 (R-GUI-10, I-9): el DUEÑO también escribe. ⛔ El agente NO, y esa
  //    es la mitad que no se puede perder de vista: `rol !== "agente"` sería lo
  //    mismo hoy y abriría la puerta al primer rol nuevo que alguien invente.
  //    Aquí se ENUMERA quién puede, nunca quién no. La pantalla no es la guarda
  //    —la de verdad vive dentro de las funciones de la migración `0056`— pero
  //    una pantalla que ofrece un botón que la base va a rechazar es una mentira
  //    en la cara del agente.
  const esDueno = rol === "dueno";
  const puedeEditar = rol === "victorix" || rol === "dueno";
  // ⛔ T-ENT-GUION-2: con secciones en uso, editar este texto no cambiaría al bot ⇒ nadie lo edita.
  const editaTexto = puedeEditar && !reemplazado;

  const [guion, setGuion] = useState<GuionActivo | null>(null);
  const [versiones, setVersiones] = useState<VersionGuion[]>([]);
  const [borrador, setBorrador] = useState<GuionPartido | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [refresco, setRefresco] = useState(0);

  useEffect(() => {
    let vivo = true;
    void (async () => {
      try {
        const g = await leerGuionActivo(sb, tenantId);
        // ⛔ La lista SOLO se pide cuando se puede editar. Para el dueño esta
        //    pantalla hace exactamente UNA consulta, como en T-GUI-10 (V-53).
        const vs = puedeEditar ? await leerVersionesGuion(sb, tenantId) : [];
        if (!vivo) return;
        setGuion(g);
        setVersiones(vs);
        setBorrador(g ? partirGuion(g.bloque_negocio) : null);
        setError(null);
      } catch (e) {
        if (!vivo) return;
        // ⛔ Traducido SIEMPRE. El crudo de Postgres en la cara del cliente es
        // el patrón que R-GUI-03 prohíbe con todas sus letras.
        setError(explicarFalloDeGuion(e));
      } finally {
        if (vivo) setCargando(false);
      }
    })();
    return () => { vivo = false; };
  }, [sb, tenantId, puedeEditar, refresco]);

  const cambiarBloque = useCallback((clave: string, texto: string) => {
    setBorrador((b) => b && ({
      ...b,
      bloques: b.bloques.map((x) => (x.clave === clave ? { ...x, texto } : x)),
    }));
  }, []);

  const cambiarSinRepartir = useCallback((texto: string) => {
    setBorrador((b) => b && ({ ...b, sinRepartir: texto }));
  }, []);

  async function alGuardar() {
    if (!borrador || ocupado) return;
    setOcupado(true); setError(null); setAviso(null);
    try {
      const nueva = await guardarGuion(sb, tenantId, unirGuion(borrador));
      setAviso(
        `Guardada la versión ${nueva.version}. El bot la toma en hasta ` +
        `${MINUTOS_HASTA_QUE_EL_BOT_LO_TOMA} minutos.`,
      );
      setRefresco((n) => n + 1);
    } catch (e) {
      setError(explicarFalloAlGuardar(e));
    } finally {
      setOcupado(false);
    }
  }

  async function alVolver(version: number) {
    if (ocupado) return;
    setOcupado(true); setError(null); setAviso(null);
    try {
      await activarVersionGuion(sb, tenantId, version);
      setAviso(
        `El bot vuelve a la versión ${version}. La toma en hasta ` +
        `${MINUTOS_HASTA_QUE_EL_BOT_LO_TOMA} minutos.`,
      );
      setRefresco((n) => n + 1);
    } catch (e) {
      setError(explicarFalloAlGuardar(e));
    } finally {
      setOcupado(false);
    }
  }

  const partido = editaTexto ? borrador : (guion ? partirGuion(guion.bloque_negocio) : null);
  const hayCambios = !!(guion && borrador && unirGuion(borrador) !== guion.bloque_negocio);

  return (
    <div className="h-full flex flex-col min-w-0">
      <header className="shrink-0 px-4 sm:px-6 pt-4 pb-3 border-b border-borde-suave">
        <h1 className="text-[19px] font-semibold tracking-tight">El guion de tu bot</h1>
        <p className="text-[12.5px] text-tinta-2 mt-0.5">
          Cómo se comporta: qué dice, qué pregunta y qué no hace nunca.
        </p>
      </header>

      <div className="flex-1 overflow-y-auto scroll-fino px-4 sm:px-6 py-4">
        <div className="max-w-[680px] mx-auto w-full space-y-3">
          {error && <Aviso tono="peligro" onCerrar={() => setError(null)}>{error}</Aviso>}
          {aviso && <Aviso tono="ok" onCerrar={() => setAviso(null)}>{aviso}</Aviso>}

          {/* T-GUI-6 (R-GUI-10) · se dice quién escribe y qué pasa al guardar,
              ANTES de que nadie lo busque. Al dueño se le explica la RED (nace
              una versión nueva y se puede volver atrás), porque es lo único que
              hace que tocar el guion del bot que vende no dé pánico; al agente
              se le dice quién sí puede, para que no crea que el panel se rompió. */}
          <Aviso tono="alerta">
            {esDueno
              ? <>Este guion lo escribes tú. Cada vez que guardas nace una versión NUEVA: la de
                  ahora queda entera, y si algo sale mal vuelves a ella desde la lista de abajo. El
                  bot toma la versión activa en hasta {MINUTOS_HASTA_QUE_EL_BOT_LO_TOMA} minutos.</>
              : puedeEditar
                ? <>Cada cambio se guarda como una versión NUEVA: la anterior queda entera y se puede
                    volver a ella. El bot toma la versión activa en hasta {MINUTOS_HASTA_QUE_EL_BOT_LO_TOMA} minutos.</>
                : <>Esta pantalla es para mirar: el guion lo escribe el dueño del negocio (o
                    Victorix). Es lo único que puede dejar mudo al bot.</>}
          </Aviso>

          {reemplazado && (
            <Aviso tono="alerta"><span data-aviso-reemplazado>{AVISO_TEXTO_ANTERIOR}</span></Aviso>
          )}

          {cargando && <SkeletonLista filas={4} />}

          {!cargando && !guion && (
            <div data-sin-guion className="border border-borde rounded-xl bg-superficie p-5 text-center">
              <p className="text-[14px] font-semibold">Este negocio todavía no tiene guion</p>
              <p className="text-[12.5px] text-tinta-2 mt-1.5 leading-snug">
                Mientras no lo tenga, el bot no contesta: prefiere callarse antes que inventar.
                Lo escribe Victorix cuando se pone en marcha el negocio.
              </p>
            </div>
          )}

          {!cargando && guion && partido && (<>
            <p className="text-[12.5px] text-tinta-2 leading-snug px-1">
              Versión {guion.version}, activa desde el {fecha(guion.creado_en)}.
            </p>

            {(partido.sinRepartir || editaTexto) && (
              <section data-bloque="sin-repartir"
                className="border border-borde rounded-xl bg-superficie p-4">
                <h2 className="text-[14px] font-semibold flex items-center gap-2">
                  <span className="text-tinta-2"><FileText size={15} /></span>
                  Todavía sin repartir
                </h2>
                <p className="text-[12.5px] text-tinta-2 mt-1 leading-snug">
                  Esto es el guion tal como está escrito hoy, de corrido. Cuando se reparta en las
                  secciones de abajo, se podrán mirar sus reglas una al lado de la otra.
                </p>
                {editaTexto
                  ? <CajaEditable valor={partido.sinRepartir} etiqueta="Todavía sin repartir"
                      onCambiar={cambiarSinRepartir} />
                  : <p className="text-[13px] mt-2.5 whitespace-pre-wrap leading-relaxed">
                      {partido.sinRepartir}
                    </p>}
              </section>
            )}

            {partido.bloques.map((b) => (
              <Bloque key={b.clave} b={b}
                editable={editaTexto}
                onCambiar={(t) => cambiarBloque(b.clave, t)} />
            ))}

            {editaTexto && (
              <div className="flex items-center justify-end gap-3 pt-1">
                <p className="text-[12px] text-tinta-2">
                  {hayCambios
                    ? "Se guardará como una versión nueva; la de ahora queda entera."
                    : "Sin cambios por guardar."}
                </p>
                <Boton tono="marca" onClick={alGuardar} disabled={!hayCambios || ocupado}>
                  Guardar como versión nueva
                </Boton>
              </div>
            )}

            {editaTexto && (
              <ListaDeVersiones versiones={versiones} usuarioId={usuarioId}
                ocupado={ocupado} onVolver={alVolver} />
            )}
          </>)}
          {/* T-ENT-GUION-1 · P2 · lo que el anfitrión ponga DEBAJO (el editor de secciones, en
              Entrenamiento). Prop y no import: esta pantalla sola sigue leyendo SOLO prompts_bot. */}
          {!cargando && debajo}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Un bloque. ⛔ Se pinta AUNQUE ESTÉ VACÍO (V-52): un bloque en blanco es la
 * única forma de que el dueño se entere de que a su bot le falta decir «qué
 * nunca haces» — esconderlo lo deja tan invisible como estaba.
 */
function Bloque({ b, editable, onCambiar }: {
  b: BloqueGuion; editable: boolean; onCambiar: (texto: string) => void;
}) {
  return (
    <section data-bloque={b.clave} data-conocido={b.conocido ? "si" : "no"}
      className="border border-borde rounded-xl bg-superficie p-4">
      <h2 className="text-[14px] font-semibold">{b.nombre}</h2>
      {b.ayuda && <p className="text-[12.5px] text-tinta-2 mt-0.5 leading-snug">{b.ayuda}</p>}
      {editable
        ? <CajaEditable valor={b.texto} etiqueta={b.nombre} onCambiar={onCambiar} />
        : b.texto
          ? <p data-texto className="text-[13px] mt-2.5 whitespace-pre-wrap leading-relaxed">{b.texto}</p>
          : <p data-vacio className="text-[12.5px] text-tinta-2 mt-2.5 italic">
              Sin escribir todavía.
            </p>}
    </section>
  );
}

/** La caja de escribir. Nada de `contenteditable`: un `<textarea>` con etiqueta. */
function CajaEditable({ valor, etiqueta, onCambiar }: {
  valor: string; etiqueta: string; onCambiar: (texto: string) => void;
}) {
  return (
    <textarea
      aria-label={etiqueta}
      value={valor}
      rows={Math.max(3, Math.min(12, valor.split("\n").length + 1))}
      onChange={(e) => onCambiar(e.target.value)}
      placeholder="Sin escribir todavía."
      className="w-full mt-2.5 text-[13px] leading-relaxed bg-elevado border border-borde
                 rounded-lg p-2.5 scroll-fino resize-y"
    />
  );
}

/**
 * La lista de versiones (V-66).
 *
 * ⛔ LA ACTIVA SE MARCA, Y NO ES ADORNO. Sin la marca se vuelve a una versión
 * creyendo que se vuelve a otra, y el bot cambia de guion sin que nadie sepa a
 * cuál. Por eso la fila activa lleva su chip Y no ofrece «volver a esta»: no
 * hay a dónde volver desde donde ya se está.
 */
function ListaDeVersiones({ versiones, usuarioId, ocupado, onVolver }: {
  versiones: VersionGuion[]; usuarioId: string | null; ocupado: boolean;
  onVolver: (version: number) => void;
}) {
  if (versiones.length === 0) return null;
  return (
    <section data-versiones className="border border-borde rounded-xl bg-superficie p-4 mt-2">
      <h2 className="text-[14px] font-semibold">Versiones</h2>
      <p className="text-[12.5px] text-tinta-2 mt-0.5 leading-snug">
        Ninguna se borra. Volver atrás es encender otra.
      </p>
      <ul className="mt-2.5 divide-y divide-borde-suave">
        {versiones.map((v) => (
          <li key={v.id} data-version={v.version} data-activa={v.activo ? "si" : "no"}
            className="flex items-center gap-3 py-2">
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-medium">
                Versión {v.version}
                {v.activo && (
                  <span data-activa-chip
                    className="ml-2 text-[11px] font-semibold text-marca align-middle">
                    Activa ahora
                  </span>
                )}
              </p>
              <p className="text-[12px] text-tinta-2">
                {fechaYHora(v.creado_en)} · la dejó {quienLaCreo(v, usuarioId)}
              </p>
            </div>
            {!v.activo && (
              <Boton tam="sm" tono="neutro" disabled={ocupado}
                onClick={() => onVolver(v.version)}>
                <RotateCcw size={14} className="mr-1.5" />
                Volver a esta
              </Boton>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
